import { useCreateSession } from '@/api/create-session.ts'
import { GetUserDetailsType, getUserDetailsQueryKey, useUserDetails } from '@/api/get-user-details.ts'
import { useMakeLogout } from '@/api/logout.ts'
import { AppAbility } from '@/app/permissions'
import defineAbilityForUser from '@/lib/casl.ts'
import { eventsManager } from '@/utils/events-manager.ts'
import { useQueryClient } from '@tanstack/react-query'
import { PropsWithChildren, createContext, useCallback, useMemo } from 'react'

export interface AuthContextProps {
  logout: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  permissions: AppAbility
  isPendingUser: boolean
  user: GetUserDetailsType | null
}

export const AuthContext = createContext<AuthContextProps | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()

  const { data: user, isPending: isPendingUser } = useUserDetails({
    enabled: false,
  })

  const logoutMutation = useMakeLogout()
  const createSessionMutation = useCreateSession()

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync({ refreshToken: null })
    } finally {
      queryClient.clear()
      eventsManager.dispatch('auth:unauthenticated')
    }
  }, [logoutMutation.mutateAsync, queryClient])

  const login = useCallback(
    async (email: string, password: string) => {
      await createSessionMutation.mutateAsync({ email, password })

      await queryClient.invalidateQueries({ queryKey: getUserDetailsQueryKey() })
    },
    [createSessionMutation.mutateAsync, queryClient],
  )

  const ability = useMemo(
    () =>
      user
        ? defineAbilityForUser({
            isTelegramSubscriber: !!user?.isTelegramSubscriber,
            trialQuoteLimit: user?.trialQuoteLimit ?? null,
            notionDatabaseId: user?.notionDatabaseId,
            role: user?.role || 'USER',
            id: user?.id || '',
            emailConfirmed: user?.emailConfirmed ?? false,
          })
        : defineAbilityForUser(null),
    [user],
  )

  const value = useMemo(
    () => ({
      logout,
      login,
      permissions: ability,
      isPendingUser,
      user: user ?? null,
    }),
    [logout, login, ability, isPendingUser, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
